// The host page prints a fixed, full-viewport loader (#wait) ahead of the widget root so
// visitors never see the empty container: the 24rent.fi homepage does it from a Divi code
// module, the standalone shell from index.html. The widget is what that loader hides, so it
// can go as soon as the widget is styled and painted. Until 2.3.7 it went on window `load`,
// which on the homepage also waits for every image, the consent script and the tag manager —
// measured on 22 Sep 2026 at 1.1 to 1.3 s after the widget had rendered on desktop, and
// 2.1 s on a phone, all of it spent looking at a spinner over a finished page.
//
// Styled means the plugin's stylesheets have arrived. WordPress prints them in the footer,
// ahead of the entry module but not necessarily loaded before it runs; lifting the loader
// over an unstyled widget would trade a spinner for a flash of raw markup.
const PLUGIN_STYLESHEET = 'link[rel="stylesheet"][href*="24rent-reservation-machine/"]';

/** Calls `hide` once the plugin stylesheets are applied, or on window load, whichever is first. */
export function hideHostLoaderWhenReady(hide: () => void): () => void {
    let done = false;
    const once = () => {
        if (done) return;
        done = true;
        cleanup();
        hide();
    };
    const pending = Array.from(document.querySelectorAll<HTMLLinkElement>(PLUGIN_STYLESHEET))
        .filter(link => !link.sheet);
    let remaining = pending.length;
    const onSheet = () => { if (--remaining <= 0) once(); };
    const cleanup = () => {
        window.removeEventListener('load', once);
        pending.forEach(link => {
            link.removeEventListener('load', onSheet);
            link.removeEventListener('error', onSheet);
        });
    };
    if (document.readyState === 'complete' || remaining === 0) {
        once();
        return cleanup;
    }
    // A stylesheet that fails is not coming; do not hold the page hostage to it.
    pending.forEach(link => {
        link.addEventListener('load', onSheet);
        link.addEventListener('error', onSheet);
    });
    window.addEventListener('load', once);
    return cleanup;
}
